import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  TextField,
  useMediaQuery,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  DialogActions,
  IconButton,
  InputAdornment,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { callApiWithStreaming } from "./api"; // Adjust import path as needed
import BlurryBackdrop from "./BlurryBackdrop";
import SendIcon from "@mui/icons-material/Send";
import { useTheme } from "@mui/material/styles";
import MessageBubble from "./MessageBubble"; // Import the MessageBubble component

import { MathJaxContext, MathJax } from "better-react-mathjax";
import { getStyles } from "../index";
import DOMPurify from "dompurify";
import { marked } from "marked";
import Logger from "./Logger";
import { Chip, Stack } from "@mui/material";

import { db } from "./firebase";
import { useAuth } from "./useAuth";
import {
  collection,
  addDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  doc,
  onSnapshot,
} from "firebase/firestore";

const AIDialog = ({
  open,
  onClose,
  initialQuestion,
  systemMessage,
  theme,
  userId,
}) => {
  const [aiResponse, setAiResponse] = useState("");
  const [followUpQuestion, setFollowUpQuestion] = useState("");
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [isMessageSending, setIsMessageSending] = useState(false);
  const [streamingId, setStreamingId] = useState(null);
  const abortControllerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [isTextFieldFocused, setIsTextFieldFocused] = useState(false);

  const themeCurrent = useTheme();
  const styles = getStyles(theme);

  const { user } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const [groupInstructions, setGroupInstructions] = useState([]);

  // Add this useEffect using the existing user data
  useEffect(() => {
    if (!user?.uid || !user?.rooms) return;

    async function fetchRoomShortcuts() {
      // If user has rooms, get shortcuts from the first one
      if (user.rooms && user.rooms.length > 0) {
        try {
          const roomDoc = await getDoc(doc(db, "rooms", user.rooms[0]));
          if (roomDoc.exists()) {
            setGroupInstructions(roomDoc.data()?.groupInstructions || []);

            Logger.log(
              "groupInstructions: ",
              roomDoc.data()?.groupInstructions,
            );
          }
        } catch (error) {
          console.error("Error fetching room:", error);
        }
      }
    }

    fetchRoomShortcuts();
  }, [user]);

  // For handleDirectChipClick
  // For handleDirectChipClick
  const handleDirectChipClick = async (promptText) => {
    if (!promptText || !promptText.trim()) return;
    setIsMessageSending(true);

    const newStreamingId = "streaming-" + Date.now();
    setStreamingId(newStreamingId);

    try {
      let streamingContent = "";
      const onChunk = (chunk) => {
        streamingContent += chunk;
        setAiResponse((prevResponse) => prevResponse + chunk);
      };

      setAiResponse(
        (prevResponse) =>
          prevResponse + "\n\nFöljdfråga: " + promptText + "\n\nAI: ",
      );

      // Simply concatenate groupInstructions (string) to the systemMessage
      const enhancedSystemMessage =
        systemMessage + (groupInstructions ? "\n\n" + groupInstructions : "");

      await callApiWithStreaming(
        [
          {
            role: "system",
            content: enhancedSystemMessage,
          },
          { role: "assistant", content: aiResponse },
          { role: "user", content: promptText },
        ],
        onChunk,
        userId,
        abortControllerRef.current.signal,
      );

      setFollowUpQuestion("");
    } catch (error) {
      if (error.name === "AbortError") {
        Logger.log("Stream was aborted");
      } else {
        console.error("Error calling AI API:", error);
        setAiResponse(
          (prevResponse) =>
            prevResponse +
            "\n\nSorry, there was an error communicating with the AI. Please try again.",
        );
      }
    } finally {
      setIsMessageSending(false);
      setStreamingId(null);
    }
  };

  // For handleAskAI
  const handleAskAI = async () => {
    setIsAiThinking(true);
    setAiResponse("");

    const newStreamingId = "streaming-" + Date.now();
    setStreamingId(newStreamingId);

    try {
      let streamingContent = "";
      const onChunk = (chunk) => {
        streamingContent += chunk;
        setAiResponse(streamingContent);
      };

      // Simply concatenate groupInstructions (string) to the systemMessage
      const enhancedSystemMessage =
        systemMessage +
        (groupInstructions ? "\n\n" + groupInstructions : "") +
        `
Om det är matematikfrågor: När du använder LaTeX-syntax MÅSTE du omsluta VARJE matematiskt uttryck (oavsett om det är enkla operationer eller komplexa ekvationer) med dollartecken på följande sätt:

För uttryck inom textrader: $x \\times y$
För fristående uttryck: $$x \\times y$$

Använd ALDRIG [], () eller andra tecken runt matematiska uttryck. ALLTID dollartecken.
`;

      await callApiWithStreaming(
        [
          {
            role: "system",
            content: enhancedSystemMessage,
          },
          { role: "user", content: initialQuestion },
        ],
        onChunk,
        userId,
        abortControllerRef.current.signal,
      );
    } catch (error) {
      if (error.name === "AbortError") {
        Logger.log("Stream was aborted");
      } else {
        console.error("Error calling AI API:", error);
        setAiResponse(
          "Sorry, there was an error communicating with the AI. Please try again.",
        );
      }
    } finally {
      setIsAiThinking(false);
      setStreamingId(null);
    }
  };

  // For handleFollowUpQuestion
  const handleFollowUpQuestion = async () => {
    if (!followUpQuestion.trim()) return;
    setIsMessageSending(true);

    const newStreamingId = "streaming-" + Date.now();
    setStreamingId(newStreamingId);

    try {
      let streamingContent = "";
      const onChunk = (chunk) => {
        streamingContent += chunk;
        setAiResponse((prevResponse) => prevResponse + chunk);
      };

      setAiResponse(
        (prevResponse) =>
          prevResponse + "\n\nFöljdfråga: " + followUpQuestion + "\n\nAI: ",
      );

      // Simply concatenate groupInstructions (string) to the systemMessage
      const enhancedSystemMessage =
        systemMessage +
        (groupInstructions ? "\n\n" + groupInstructions : "") +
        `
Om det är matematikfrågor: När du använder LaTeX-syntax MÅSTE du omsluta VARJE matematiskt uttryck (oavsett om det är enkla operationer eller komplexa ekvationer) med dollartecken på följande sätt:

För uttryck inom textrader: $x \\times y$
För fristående uttryck: $$x \\times y$$

Använd ALDRIG [], () eller andra tecken runt matematiska uttryck. ALLTID dollartecken.
`;

      await callApiWithStreaming(
        [
          {
            role: "system",
            content: enhancedSystemMessage,
          },
          { role: "assistant", content: aiResponse },
          { role: "user", content: followUpQuestion },
        ],
        onChunk,
        userId,
        abortControllerRef.current.signal,
      );

      setFollowUpQuestion("");
    } catch (error) {
      if (error.name === "AbortError") {
        Logger.log("Stream was aborted");
      } else {
        console.error("Error calling AI API:", error);
        setAiResponse(
          (prevResponse) =>
            prevResponse +
            "\n\nSorry, there was an error communicating with the AI. Please try again.",
        );
      }
    } finally {
      setIsMessageSending(false);
      setStreamingId(null);
    }
  };

  const handleSendMessage = async () => {
    if (!followUpQuestion.trim()) return;
    await handleFollowUpQuestion();
  };

  const parseMarkdownWithCursor = (content, isStreaming) => {
    const cursorPlaceholder = "∎";
    let contentWithPlaceholder = content;

    if (isStreaming) {
      contentWithPlaceholder += cursorPlaceholder;
    }

    const parsedContent = marked(contentWithPlaceholder);
    const sanitizedContent = DOMPurify.sanitize(parsedContent);
    return sanitizedContent.replace(
      cursorPlaceholder,
      '<span class="pulsating-dot"></span>',
    );
  };

  const processMessages = (aiResponse, isStreaming) => {
    return aiResponse
      .split("\n\n")
      .reduce((messages, message, index, array) => {
        if (message.startsWith("Följdfråga:")) {
          messages.push({
            content: message.replace("Följdfråga: ", ""),
            isUser: true,
          });
        } else if (message.startsWith("AI:")) {
          const content = message.replace("AI: ", "");
          messages.push({
            content: parseMarkdownWithCursor(
              content,
              isStreaming && index === array.length - 1,
            ),
            isUser: false,
          });
        } else if (
          messages.length > 0 &&
          !messages[messages.length - 1].isUser
        ) {
          const lastMessage = messages[messages.length - 1];
          lastMessage.content = parseMarkdownWithCursor(
            lastMessage.content + "\n\n" + message,
            isStreaming && index === array.length - 1,
          );
        } else {
          messages.push({
            content: parseMarkdownWithCursor(
              message,
              isStreaming && index === array.length - 1,
            ),
            isUser: false,
          });
        }
        return messages;
      }, []);
  };

  useEffect(() => {
    if (open) {
      abortControllerRef.current = new AbortController();
      handleAskAI();

      Logger.log("initialQuestion: ", initialQuestion);
    }
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [aiResponse]);

  const handleClose = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: "450px", md: "550px" },
          backgroundColor: theme.palette.mode === "dark" ? "#22303C" : "white",
          backgroundImage: theme.palette.mode === "dark" ? "none" : "white",
          // Don't set padding on the main container since we'll control it in the child elements
        },
      }}
    >
      <MathJaxContext
        config={{
          tex: {
            inlineMath: [
              ["$", "$"],
              ["\\(", "\\)"],
            ],
            displayMath: [
              ["$$", "$$"],
              ["\\[", "\\]"],
            ],
          },
          chtml: {
            scale: 1.3, // Global scaling factor
            fontFamily: "Arial", // Font to use
            matchFontHeight: false, // Match surrounding font height
            displayAlign: "left", // How to align displayed equations
            displayIndent: "0", // Indent for displayed equations
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%", // Take full height of the drawer
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 3,
              //borderBottom: 1,
              //borderColor: "divider",
              flexShrink: 0, // Prevent header from shrinking
            }}
          >
            <Typography sx={{ ...styles.h3, fontWeight: 700 }} variant="title">
              Fråga AI
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                color: theme.palette.mode === "dark" ? "white" : "black",
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Messages container - this will scroll */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flexGrow: 1, // Take available space
              overflowY: "auto", // Enable scrolling
              p: 2,
            }}
          >
            {processMessages(aiResponse, !!streamingId).map(
              (message, index) => (
                <MessageBubble
                  key={index}
                  role={message.isUser ? "user" : "assistant"}
                  content={message.content}
                  id={`message-${index}`}
                  isStreaming={
                    !message.isUser &&
                    !!streamingId &&
                    index ===
                      processMessages(aiResponse, !!streamingId).length - 1
                  }
                  isDark={theme.palette.mode === "dark"}
                  userId={userId}
                  avatar={message.isUser ? undefined : "/img/AI.png"}
                  latexActivated={true}
                  // Set to true to disable unneeded features like save/bookmark, generate ideas, etc.
                  // Only export and copy buttons will be shown
                  isSimplified={true}
                />
              ),
            )}
            <div ref={messagesEndRef} />
          </Box>
          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: "divider",
              backgroundColor:
                theme.palette.mode === "dark" ? "#22303C" : "white",
              flexShrink: 0, // Prevent from shrinking
            }}
          >
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip
                label="Förenkla"
                onClick={() =>
                  handleDirectChipClick("Förklara på ett enklare sätt")
                }
                sx={{
                  bgcolor:
                    theme.palette.mode === "dark" ? "#1E2A35" : "#f0f0f0",
                  "&:hover": {
                    bgcolor:
                      theme.palette.mode === "dark" ? "#2C3B47" : "#e0e0e0",
                  },
                }}
              />
              <Chip
                label="Förklara steg för steg"
                onClick={() => handleDirectChipClick("Förklara steg för steg")}
                sx={{
                  bgcolor:
                    theme.palette.mode === "dark" ? "#1E2A35" : "#f0f0f0",
                  "&:hover": {
                    bgcolor:
                      theme.palette.mode === "dark" ? "#2C3B47" : "#e0e0e0",
                  },
                }}
              />
              <Chip
                label="Ge exempel"
                onClick={() => handleDirectChipClick("Ge exempel")}
                sx={{
                  bgcolor:
                    theme.palette.mode === "dark" ? "#1E2A35" : "#f0f0f0",
                  "&:hover": {
                    bgcolor:
                      theme.palette.mode === "dark" ? "#2C3B47" : "#e0e0e0",
                  },
                }}
              />
              <Chip
                label="Förklara i en mening"
                onClick={() => handleDirectChipClick("Förklara i en mening")}
                sx={{
                  bgcolor:
                    theme.palette.mode === "dark" ? "#1E2A35" : "#f0f0f0",
                  "&:hover": {
                    bgcolor:
                      theme.palette.mode === "dark" ? "#2C3B47" : "#e0e0e0",
                  },
                }}
              />
            </Stack>

            <TextField
              fullWidth
              placeholder="Ställ en följdfråga"
              value={followUpQuestion}
              onChange={(e) => setFollowUpQuestion(e.target.value)}
              onKeyPress={handleKeyPress}
              autoFocus={!isMobile}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {isMessageSending ? (
                      <CircularProgress size={20} />
                    ) : (
                      <IconButton
                        onClick={handleSendMessage}
                        disabled={isMessageSending}
                        sx={{
                          color: theme.palette.primary.main,
                          "&:hover": {
                            color: theme.palette.primary.dark,
                          },
                        }}
                      >
                        <SendIcon />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
              onFocus={() => setIsTextFieldFocused(true)}
              onBlur={() => setIsTextFieldFocused(false)}
            />
          </Box>
        </Box>
      </MathJaxContext>
    </Drawer>
  );
};

export default AIDialog;
